/**
 * Вход и регистрация.
 *
 * Два способа войти: почта (или имя) с паролем — и Telegram. Второй нужен как
 * раз тогда, когда пароль не вспоминается: аккаунт уже привязан к чату, бот
 * подтверждает вход одним нажатием Start.
 *
 * Из оформления убран рекламный подзаголовок: на странице входа он ничего не
 * сообщает тому, кто уже пришёл вводить пароль.
 *
 * 16.10 — в дизайне терминала: карточка с подсвеченной кромкой, переключатель
 * SegTabs, поля со значками, код 2FA шестью клетками (ввод — одно настоящее
 * поле поверх них), кнопка Telegram в его цвете. Все способы входа и их
 * логика (пароль + 2FA, бот с продолжением по localStorage, виджет, passkey,
 * регистрация) не менялись — только вид.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../store/useStore';
import { apiClient } from '../api/client';
import { wsManager } from '../api/websocket';
import toast from 'react-hot-toast';
import { Eye, EyeOff, ArrowRight, Send, Loader2, Fingerprint, UserRound, Mail, Lock, ShieldCheck } from 'lucide-react';
import { SegTabs } from '../ui/SegTabs';
import { getPasskey, isPasskeyCancel, passkeysSupported } from '../utils/webauthn';
import { tgInitData } from '../utils/telegram';
import { useTranslation } from 'react-i18next';

type Mode = 'login' | 'register';

// Неоконченный вход через бота: {token, deep_link, at} — см. pollTelegram.
const TG_PENDING_KEY = 'tg_login_pending';

export const Login: React.FC = () => {
  const { t } = useTranslation('login');
  const [mode, setMode] = useState<Mode>('login');
  // Открыта ли регистрация — спрашиваем у бэкенда до входа.
  const [regOpen, setRegOpen] = useState(false);
  useEffect(() => {
    apiClient.raw.get('/auth/registration')
      .then(r => setRegOpen(!!r.data?.open))
      .catch(() => setRegOpen(false));
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', password: '' });

  const { isAuthenticated, setAuthenticated, setUser } = useAppStore();
  // Куда вернуть после входа: Protected кладёт сюда адрес вместе с query.
  // Иначе панель автозалива на fundoor после входа теряла пару из ссылки и
  // открывалась терминалом с ETHUSDT.
  const fromState = (useLocation().state as { from?: unknown } | null)?.from;
  const backTo = typeof fromState === 'string' && fromState.startsWith('/')
    && !fromState.startsWith('//') && !fromState.startsWith('/login') ? fromState : '/';

  // ── Вход через Telegram ───────────────────────────────────────────────────
  // Просим у бэка одноразовый код, открываем deep-link и опрашиваем: как только
  // бот подтвердил — получаем токен. Опрос глушим на размонтировании, иначе он
  // продолжает стучать после ухода со страницы.
  const [tg, setTg] = useState<{ token: string; deep_link: string | null } | null>(null);
  // Включена 2FA: пароль (или Telegram) принят, ждём код из приложения.
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeBusy, setCodeBusy] = useState(false);
  const submitCode = async (value: string) => {
    if (!challenge || value.length !== 6 || codeBusy) return;
    setCodeBusy(true);
    try {
      await apiClient.verify2fa(challenge, value);
      setChallenge(null);
      await finishAuth(t('greeting.welcomeBack'));
    } catch (e: any) {
      setCode('');
      const msg = e?.response?.data?.detail || t('errors.badCode');
      toast.error(msg);
      // Challenge сгорел (время вышло или много ошибок) — назад к паролю.
      // Текст ошибки приходит на языке интерфейса (сервер переводит по X-Lang).
      if (/заново|знову/.test(msg)) setChallenge(null);
    } finally { setCodeBusy(false); }
  };
  const [tgWaiting, setTgWaiting] = useState(false);
  const pollRef = useRef<number | null>(null);

  // Открыли из Telegram (Mini App) — входим сами по подписанному initData:
  // ни пароля, ни ожидания бота. С 2FA — тот же экран кода; с запомненного
  // устройства код не спросят.
  const [waBusy, setWaBusy] = useState(() => !!tgInitData() && !isAuthenticated);
  useEffect(() => {
    const init = tgInitData();
    if (!init || isAuthenticated) return;
    (async () => {
      try {
        const r = await apiClient.raw.post('/auth/telegram/webapp', {
          init_data: init, device_token: apiClient.getDeviceToken() || undefined,
        });
        if (r.data?.access_token) {
          apiClient.setAuth(r.data.access_token);
          await finishAuth(t('greeting.telegram'));
        } else if (r.data?.need_2fa && r.data?.challenge) {
          setChallenge(r.data.challenge);
        }
      } catch (e: any) {
        const msg = e?.response?.data?.detail || t('errors.telegramFailed');
        // 404 — этот Telegram ещё не знаком: войти паролем один раз, и он
        // запомнится. Это не ошибка, а первый шаг.
        if (e?.response?.status === 404) toast(msg, { icon: '🔗', duration: 6000 });
        else toast.error(msg);
      } finally {
        setWaBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => () => { if (pollRef.current) window.clearInterval(pollRef.current); }, []);

  const finishAuth = async (greeting: string) => {
    setAuthenticated(true);
    await wsManager.connect();
    try { setUser(await apiClient.getSettings()); } catch { /* профиль подтянется позже */ }
    toast.success(greeting);
  };

  // Вход по passkey: устройство само предлагает сохранённый ключ и проверяет
  // владельца (палец, лицо, PIN) — ни логина, ни пароля, ни кода не нужно.
  const [pkBusy, setPkBusy] = useState(false);
  const loginPasskey = async () => {
    if (pkBusy) return;
    setPkBusy(true);
    try {
      const { data } = await apiClient.raw.post('/auth/passkey/login/options');
      const credential = await getPasskey(data.options);
      const r = await apiClient.raw.post('/auth/passkey/login/verify',
        { challenge_id: data.challenge_id, credential });
      if (!r.data?.access_token) throw new Error('no token');
      localStorage.setItem('access_token', r.data.access_token);
      await finishAuth(t('greeting.welcomeBack'));
    } catch (e: any) {
      if (!isPasskeyCancel(e)) toast.error(e?.response?.data?.detail || t('errors.passkeyFailed'));
    } finally { setPkBusy(false); }
  };

  // Авторизация сайта через Telegram (официальный Telegram Login): окно
  // oauth.telegram.org — номер телефона и подтверждение в самом Telegram.
  // Доступна, если домен сайта привязан к боту в BotFather; сервер это знает
  // (/auth/telegram/widget). Скрипт Telegram грузим заранее: окно должно
  // открыться прямо из клика, иначе браузер его заблокирует.
  const [tgWidget, setTgWidget] = useState<{ bot_id: string } | null>(null);
  useEffect(() => {
    let alive = true;
    apiClient.raw.get('/auth/telegram/widget').then(r => {
      if (!alive || !r.data?.enabled || !r.data?.bot_id) return;
      const ready = () => { if (alive) setTgWidget({ bot_id: String(r.data.bot_id) }); };
      if ((window as any).Telegram?.Login) { ready(); return; }
      const sc = document.createElement('script');
      sc.src = 'https://telegram.org/js/telegram-widget.js?22';
      sc.async = true;
      sc.onload = ready;
      document.head.appendChild(sc);
    }).catch(() => { /* останется вход через бота */ });
    return () => { alive = false; };
  }, []);
  const [tgAuthBusy, setTgAuthBusy] = useState(false);
  // Окно Telegram закрылось, а данных входа страница не получила (09.10:
  // Telegram написал «Вы авторизовались», сайт не пустил). Окно отдаёт данные
  // сообщением странице; если оно потерялось, скрипт Telegram спрашивает их
  // запросом с чужими куками, а их браузер режет. Тогда предлагаем тот же вход
  // переходом всей страницы: Telegram вернёт данные в адресе (#tgAuthResult).
  const [tgLost, setTgLost] = useState(false);
  const sendTelegramAuth = async (data: any) => {
    setTgAuthBusy(true);
    try {
      const r = await apiClient.raw.post('/auth/telegram/widget', data);
      if (!r.data?.access_token) throw new Error('no token');
      localStorage.setItem('access_token', r.data.access_token);
      // Данные пришли в само окно Telegram (оно вернулось на сайт): токен уже
      // лежит в общем хранилище — основная вкладка подхватит его, окно закрываем.
      if (window.opener && window.name.startsWith('telegram_oauth_bot')) { window.close(); return; }
      await finishAuth(t('greeting.welcomeBack'));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('errors.telegramFailed'));
    } finally { setTgAuthBusy(false); }
  };
  const loginTelegramSite = () => {
    const login = (window as any).Telegram?.Login;
    if (!tgWidget || !login) { void startTelegram(); return; }
    setTgLost(false);
    login.auth({ bot_id: tgWidget.bot_id, request_access: 'write' }, (data: any) => {
      if (!data) { setTgLost(true); return; }  // окно закрыто, данных нет
      void sendTelegramAuth(data);
    });
  };
  const telegramRedirect = () => {
    if (!tgWidget) return;
    const back = location.origin + location.pathname;
    location.href = 'https://oauth.telegram.org/auth?bot_id=' + encodeURIComponent(tgWidget.bot_id)
      + '&origin=' + encodeURIComponent(location.origin) + '&request_access=write'
      + '&return_to=' + encodeURIComponent(back);
  };
  // Возврат от Telegram: данные входа в адресе (#tgAuthResult=<base64 JSON>).
  useEffect(() => {
    const m = location.hash.match(/[#?&]tgAuthResult=([A-Za-z0-9\-_=]*)$/);
    if (!m) return;
    history.replaceState(null, '', location.pathname + location.search);
    try {
      let b = m[1].replace(/-/g, '+').replace(/_/g, '/');
      while (b.length % 4) b += '=';
      const data = JSON.parse(decodeURIComponent(escape(window.atob(b))));
      if (data && data.id) void sendTelegramAuth(data);
    } catch { toast.error(t('errors.telegramFailed')); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Вход завершился в другом окне этого сайта (окно Telegram вернулось сюда же).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'access_token' && e.newValue) void finishAuth(t('greeting.welcomeBack'));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Опрос «подтвердил ли бот». Код входа держим и в localStorage: на телефоне
  // (особенно во встроенном браузере Telegram) переход в чат бота выгружает
  // страницу вместе с таймером, и после «Подтвердить» возвращаться было не
  // во что — код подтверждён, а спросить про него уже некому (15.10).
  const pollTelegram = (data: { token: string; deep_link: string | null }) => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    setTg(data);
    setTgWaiting(true);
    const done = () => {
      window.clearInterval(pollRef.current!);
      pollRef.current = null;
      try { localStorage.removeItem(TG_PENDING_KEY); } catch { /* нечего чистить */ }
    };
    const tick = async () => {
      try {
        const p = await apiClient.raw.get(`/auth/telegram/poll?token=${encodeURIComponent(data.token)}`);
        if (p.data?.need_2fa && p.data?.challenge) {
          done();
          setTgWaiting(false);
          setChallenge(p.data.challenge);
          return;
        }
        if (p.data?.access_token) {
          done();
          localStorage.setItem('access_token', p.data.access_token);
          setTgWaiting(false);
          await finishAuth(t('greeting.welcomeBack'));
        }
      } catch (e: any) {
        // 404 — код истёк или чат не привязан: продолжать опрос бессмысленно.
        if (e?.response?.status === 404) {
          done();
          setTgWaiting(false);
          setTg(null);
          toast.error(e?.response?.data?.detail || t('errors.codeExpired'));
        }
      }
    };
    pollRef.current = window.setInterval(() => void tick(), 2000);
    void tick();
  };
  // Вернулись на страницу входа (или её перезагрузил браузер) с неоконченным
  // входом через бота — продолжаем опрос того же кода.
  useEffect(() => {
    let saved: { token: string; deep_link: string | null; at: number } | null = null;
    try { saved = JSON.parse(localStorage.getItem(TG_PENDING_KEY) || 'null'); } catch { saved = null; }
    if (saved?.token && Date.now() - (saved.at || 0) < 5 * 60_000) pollTelegram(saved);
    else { try { localStorage.removeItem(TG_PENDING_KEY); } catch { /* нечего чистить */ } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startTelegram = async () => {
    setTgWaiting(true);
    // Окно открываем СРАЗУ, из самого клика, и только потом узнаём ссылку:
    // window.open после await браузер считает всплывающим окном и молча
    // блокирует — кнопка крутилась вечно, а бот не открывался (09.10).
    const win = window.open('', '_blank');
    try {
      const r = await apiClient.raw.post('/auth/telegram/start');
      const data = r.data as { token: string; deep_link: string | null; bot: string };
      if (!data.deep_link) {
        try { win?.close(); } catch { /* уже закрыто */ }
        toast.error(t('errors.botNotConfigured'));
        setTgWaiting(false);
        return;
      }
      try { localStorage.setItem(TG_PENDING_KEY, JSON.stringify({ ...data, at: Date.now() })); } catch { /* приватный режим */ }
      pollTelegram(data);
      if (win) {
        try { win.opener = null; } catch { /* не дали — не страшно */ }
        win.location.href = data.deep_link;
      } else {
        // Новое окно не дали (встроенный браузер Telegram) — идём к боту этой
        // же вкладкой; вернувшись, страница продолжит опрос по сохранённому коду.
        window.location.href = data.deep_link;
      }
    } catch (e: any) {
      setTgWaiting(false);
      toast.error(e?.response?.data?.detail || t('errors.telegramStartFailed'));
    }
  };

  if (isAuthenticated) return <Navigate to={backTo} replace />;
  if (waBusy) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background text-sm text-muted-foreground">
        <style>{LOGIN_CSS}</style>
        <img src="/logo.png" alt="" className="lg-logo h-14 w-14 rounded-2xl object-cover ring-1 ring-border" />
        <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {t('telegram.signingIn')}</span>
      </div>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        const r = await apiClient.login({ username: form.username.trim(), password: form.password });
        if (r.need_2fa && r.challenge) {
          setChallenge(r.challenge);
          setCode('');
          return;
        }
      } else {
        await apiClient.register({
          username: form.username.trim(), email: form.email.trim(), password: form.password,
        });
      }
      await finishAuth(mode === 'login' ? t('greeting.welcomeBack') : t('greeting.created'));
    } catch (error: any) {
      toast.error(error.response?.data?.detail
        || (mode === 'login' ? t('errors.loginFailed') : t('errors.registerFailed')));
    } finally {
      setLoading(false);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  // Поле: общий sb-input + место под значок слева.
  const field = 'sb-input !h-11 !rounded-xl !pl-10 !text-sm';

  const tabs = (regOpen ? (['login', 'register'] as const) : (['login'] as const))
    .map(m => ({ key: m as Mode, label: m === 'login' ? t('tabs.login') : t('tabs.register') }));

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-16 text-foreground">
      <style>{LOGIN_CSS}</style>
      {/* Фон: мягкое свечение + сетка. Держим тихим — это страница входа,
          а не лендинг. Цвета от темы: в светлой сетка тоже видна. */}
      <div aria-hidden className="lg-grid pointer-events-none absolute inset-0" />
      <div aria-hidden className="lg-glow pointer-events-none absolute left-1/2 top-[-18rem] h-[42rem] w-[42rem] -translate-x-1/2 rounded-full" />

      <div className="relative w-full max-w-[420px]">
        <div className="mb-7 flex flex-col items-center gap-3">
          <img src="/logo.png" alt="" className="lg-logo h-16 w-16 rounded-2xl object-cover ring-1 ring-border" />
          <div className="font-mono text-lg font-semibold tracking-tight">
            spread<span className="text-muted-foreground">.bot</span>
          </div>
        </div>

        {challenge ? (
          <div key="2fa" className="lg-card p-6 sm:p-7">
            <div className="mb-1 flex items-center gap-2.5">
              <span className="sb-badgeicon"><ShieldCheck size={16}/></span>
              <div className="text-base font-semibold">{t('twoFa.title')}</div>
            </div>
            <p className="mb-5 mt-2 text-[13px] text-muted-foreground">
              {t('twoFa.hint')}
            </p>
            {/* Шесть клеток — картинка; ввод идёт в одно настоящее поле поверх
                них (автоподстановка кода из SMS/менеджера паролей работает). */}
            <div className="lg-otp" data-busy={codeBusy}>
              <input value={code} autoFocus inputMode="numeric" autoComplete="one-time-code"
                maxLength={6} placeholder="000000" aria-label={t('twoFa.title')}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(v);
                  if (v.length === 6) void submitCode(v);
                }} />
              {Array.from({ length: 6 }, (_, i) => (
                <span key={i} data-on={i === Math.min(code.length, 5)} data-fill={i < code.length}>{code[i] || ''}</span>
              ))}
            </div>
            <button type="button" disabled={code.length !== 6 || codeBusy} onClick={() => void submitCode(code)}
              className="sb-cta mt-5 flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold">
              {codeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t('signIn')} <ArrowRight className="lg-arrow h-4 w-4" /></>}
            </button>
            <button type="button" onClick={() => { setChallenge(null); setCode(''); }}
              className="mt-3 w-full text-center text-[12px] text-muted-foreground hover:text-foreground">
              {t('common:back')}
            </button>
          </div>
        ) : (
        <div key="form" className="lg-card p-6 sm:p-7">
          {/* Переключатель вход/регистрация. Вкладку регистрации показываем,
              только если она реально открыта — иначе человек заполняет форму
              и получает 403. */}
          {tabs.length > 1 && (
            <SegTabs<Mode> className="mb-6" stretch accent value={mode} onChange={setMode} items={tabs} />
          )}

          <form onSubmit={submit} className="space-y-3.5">
            <label className="block">
              <span className="mb-1.5 block text-xs text-muted-foreground">
                {mode === 'login' ? t('fields.loginName') : t('fields.username')}
              </span>
              <span className="lg-field">
                <UserRound className="lg-ico" />
                <input name="username" value={form.username} onChange={onChange} required
                  autoComplete={mode === 'login' ? 'username' : 'nickname'}
                  placeholder={mode === 'login' ? 'you@mail.com' : 'trader'}
                  className={field} />
              </span>
            </label>

            {mode === 'register' && (
              <label className="sb-reveal block">
                <span className="mb-1.5 block text-xs text-muted-foreground">{t('fields.email')}</span>
                <span className="lg-field">
                  <Mail className="lg-ico" />
                  <input name="email" type="email" value={form.email} onChange={onChange} required
                    autoComplete="email" placeholder="you@mail.com" className={field} />
                </span>
              </label>
            )}

            <label className="block">
              <span className="mb-1.5 block text-xs text-muted-foreground">{t('fields.password')}</span>
              <span className="lg-field">
                <Lock className="lg-ico" />
                <input name="password" type={showPassword ? 'text' : 'password'}
                  value={form.password} onChange={onChange} required
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  placeholder="••••••••" className={`${field} !pr-11`} />
                <button type="button" onClick={() => setShowPassword(v => !v)}
                  className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
                  title={showPassword ? t('password.hide') : t('password.show')}
                  aria-label={showPassword ? t('password.hide') : t('password.show')}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </span>
            </label>

            <button type="submit" disabled={loading}
              className="sb-cta !mt-5 flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" />
                : <>{mode === 'login' ? t('signIn') : t('createAccount')} <ArrowRight className="lg-arrow h-4 w-4" /></>}
            </button>
          </form>

          {mode === 'login' && (
            <div className="sb-reveal">
              <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-[.08em] text-muted-foreground">
                <span className="h-px flex-1 bg-gradient-to-r from-transparent to-border" /> {t('or')} <span className="h-px flex-1 bg-gradient-to-l from-transparent to-border" />
              </div>
              <button type="button" onClick={loginTelegramSite} disabled={tgWaiting || tgAuthBusy}
                className="lg-alt lg-tg" data-wait={tgWaiting}>
                {tgWaiting || tgAuthBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {tgWaiting ? t('telegram.waiting') : t('telegram.signIn')}
              </button>
              {/* Запасной путь, когда основная кнопка открывает окно Telegram:
                  вход через бота (ссылка + «Подтвердить вход» в чате). */}
              {tgLost && !tgAuthBusy && (
                <button type="button" onClick={telegramRedirect}
                  className="sb-reveal mt-2 w-full rounded-xl border border-primary/40 bg-primary/[0.06] px-3 py-2 text-center text-[12px] text-foreground transition-colors hover:bg-primary/10">
                  {t('telegram.lost')}
                </button>
              )}
              {tgWidget && !tgWaiting && (
                <button type="button" onClick={() => void startTelegram()}
                  className="mt-2 w-full text-center text-[12px] text-muted-foreground hover:text-foreground">
                  {t('telegram.viaBot')}
                </button>
              )}
              {passkeysSupported() && (
                <button type="button" onClick={() => void loginPasskey()} disabled={pkBusy}
                  className="lg-alt mt-2">
                  {pkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />}
                  {t('passkey.signIn')}
                </button>
              )}
              {tg?.deep_link && tgWaiting && (
                <p className="sb-reveal mt-3 text-center text-[11.5px] text-muted-foreground">
                  {t('telegram.notOpened')}{' '}
                  <a href={tg.deep_link} target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-2 hover:underline">
                    {t('telegram.openBot')}
                  </a>{' '}{t('telegram.pressStart')}
                </p>
              )}
            </div>
          )}
        </div>
        )}
      </div>
      {/* Тот же знак, что в футере приложения: страница входа живёт вне AppShell. */}
      <p className="absolute inset-x-0 bottom-4 text-center font-mono text-[11px] text-muted-foreground">
        © {new Date().getFullYear()} spread.bot · {t('appFooter:rights')}
      </p>
    </div>
  );
};

const LOGIN_CSS = `
.lg-grid{opacity:.5;background-image:linear-gradient(hsl(var(--foreground) / .045) 1px,transparent 1px),linear-gradient(90deg,hsl(var(--foreground) / .045) 1px,transparent 1px);
  background-size:34px 34px;-webkit-mask-image:radial-gradient(ellipse at 50% 30%,#000 20%,transparent 75%);mask-image:radial-gradient(ellipse at 50% 30%,#000 20%,transparent 75%)}
.lg-glow{background:radial-gradient(circle at center,hsl(var(--primary) / .18) 0%,hsl(var(--primary) / 0) 62%);animation:lg-breathe 7s ease-in-out infinite}
@keyframes lg-breathe{50%{opacity:.65;transform:translateX(-50%) scale(1.06)}}
.lg-logo{box-shadow:0 14px 40px -12px hsl(var(--primary) / .7);animation:lg-in .6s cubic-bezier(.3,1.3,.4,1) both}
@keyframes lg-in{from{opacity:0;transform:translateY(-8px) scale(.85)}}
.lg-card{position:relative;border-radius:20px;border:1px solid hsl(var(--border));
  background:linear-gradient(180deg,hsl(var(--foreground) / .035),transparent 160px),hsl(var(--card) / .85);
  -webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px);
  box-shadow:0 1px 0 0 hsl(var(--foreground) / .05) inset,0 30px 70px -30px rgb(0 0 0 / .75);animation:sb-rise .5s cubic-bezier(.2,.8,.2,1) both .05s}
.lg-card::before{content:"";position:absolute;inset:-1px;border-radius:inherit;padding:1px;pointer-events:none;
  background:linear-gradient(160deg,hsl(var(--primary) / .55),transparent 35%,transparent 70%,hsl(var(--primary) / .25));
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;mask-composite:exclude}
:root:not(.dark) .lg-card{background:hsl(var(--card));box-shadow:0 1px 0 0 rgb(255 255 255 / .8) inset,0 24px 50px -24px rgb(60 40 20 / .35)}
.lg-field{position:relative;display:block}
.lg-field .lg-ico{position:absolute;left:13px;top:50%;width:16px;height:16px;transform:translateY(-50%);color:hsl(var(--muted-foreground) / .8);pointer-events:none;transition:color .2s}
.lg-field:focus-within .lg-ico{color:hsl(var(--primary))}
.lg-arrow{transition:transform .2s cubic-bezier(.3,1.4,.4,1)}
button:hover:not(:disabled) .lg-arrow{transform:translateX(3px)}
.lg-alt{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;height:44px;border-radius:12px;font-size:14px;
  border:1px solid hsl(var(--border));background:hsl(var(--foreground) / .03);transition:background-color .2s,border-color .2s,transform .15s,box-shadow .2s}
.lg-alt:hover:not(:disabled){border-color:hsl(var(--muted-foreground) / .45);background:hsl(var(--foreground) / .06);transform:translateY(-1px)}
.lg-alt:active:not(:disabled){transform:scale(.985)}
.lg-alt:disabled{opacity:.7;cursor:default}
:root:not(.dark) .lg-alt{background:hsl(var(--card))}
.lg-tg{color:#fff;border-color:transparent;background:linear-gradient(135deg,#2aabee,#229ed9);box-shadow:0 8px 22px -10px #229ed9}
.lg-tg:hover:not(:disabled){background:linear-gradient(135deg,#37b4f2,#26a7e3);border-color:transparent;box-shadow:0 12px 28px -10px #229ed9}
:root:not(.dark) .lg-tg{background:linear-gradient(135deg,#2aabee,#229ed9)}
.lg-tg[data-wait="true"]{animation:lg-pulse 1.6s ease-in-out infinite}
@keyframes lg-pulse{50%{box-shadow:0 0 0 6px rgb(34 158 217 / .18),0 8px 22px -10px #229ed9}}
.lg-otp{position:relative;display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:8px}
.lg-otp>input{position:absolute;inset:0;z-index:2;width:100%;height:100%;opacity:0;font-size:16px;cursor:text}
.lg-otp>span{display:grid;place-items:center;height:52px;border-radius:12px;font-family:'Geist Mono','JetBrains Mono',monospace;font-size:22px;font-weight:600;
  border:1px solid hsl(var(--border));background:hsl(var(--background) / .6);transition:border-color .2s,box-shadow .2s,transform .15s}
:root:not(.dark) .lg-otp>span{background:hsl(var(--card))}
.lg-otp>span[data-fill="true"]{border-color:hsl(var(--muted-foreground) / .45);animation:lg-pop .22s cubic-bezier(.3,1.5,.4,1)}
.lg-otp>input:focus ~ span[data-on="true"]{border-color:hsl(var(--primary) / .8);box-shadow:0 0 0 3px hsl(var(--primary) / .18)}
.lg-otp[data-busy="true"]>span{opacity:.6}
@keyframes lg-pop{40%{transform:scale(1.08)}}
@media (prefers-reduced-motion:reduce){
  .lg-glow,.lg-logo,.lg-card,.lg-tg,.lg-otp>span{animation:none !important}
  .lg-alt,.lg-arrow,.lg-otp>span{transition:none !important}
}
`;

export default Login;
